// Contracts — kids cabinet SHOT flow (staff) (2026-06-28). Pick checked-in child →
// start session → 4 captures (one per lighting) → finish. Shapes snapped from
// migrations/0002 (sessions, captures, children) + the old kids-worker routes/shot.ts.
import { z } from 'zod';
import { KidsGenderSchema } from '../kids';
import { KidsLightingSchema, KidsFaceExpressionSchema, KIDS_DEFAULT_FACE_EXPRESSION } from './enums';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// GET /api/kids/shot/children?date= → checked-in kids w/o a live session.
export const KidsShotChildrenQuerySchema = z.object({
  date: z.string().regex(DATE_RE),
});
export type KidsShotChildrenQuery = z.infer<typeof KidsShotChildrenQuerySchema>;

// POST /api/kids/shot/session → persist appearance + create the session row.
export const KidsShotSessionReqSchema = z.object({
  childId: z.string().min(1),
  name: z.string().trim().min(1).max(100),
  age: z.number().int().min(5).max(17),
  gender: KidsGenderSchema,
  ethnicity: z.string(),
  skinTone: z.number().int().min(1).max(10),
  hasBeard: z.boolean(),
  hasMustache: z.boolean(),
  usesReadingGlasses: z.boolean(),
  accessoryName: z.string().nullable().optional(),
});
export type KidsShotSessionReq = z.infer<typeof KidsShotSessionReqSchema>;

export const KidsShotSessionResSchema = z.object({ sessionId: z.number().int() });
export type KidsShotSessionRes = z.infer<typeof KidsShotSessionResSchema>;

// POST /api/kids/shot/session/:id/edit → edit appearance fields of an EXISTING
// session's child. sessionId comes from the path; every field is optional
// (partial patch — only sent fields are written). Ownership + status gates live
// in the handler: staff edit their own sessions, admin/manager any in office;
// started|done editable, canceled is not.
export const KidsShotSessionEditReqSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  age: z.number().int().min(5).max(17).optional(),
  gender: KidsGenderSchema.optional(),
  ethnicity: z.string().optional(),
  skinTone: z.number().int().min(1).max(10).optional(),
  hasBeard: z.boolean().optional(),
  hasMustache: z.boolean().optional(),
  usesReadingGlasses: z.boolean().optional(),
  accessoryName: z.string().nullable().optional(),
});
export type KidsShotSessionEditReq = z.infer<typeof KidsShotSessionEditReqSchema>;

// Appearance snapshot returned by GET session detail so the edit form can
// prefill (SessionRow list stays lean — these are fetched on demand).
export const KidsShotSessionAppearanceSchema = z.object({
  name: z.string(),
  age: z.number().int(),
  gender: KidsGenderSchema,
  ethnicity: z.string(),
  skinTone: z.number().int(),
  hasBeard: z.boolean(),
  hasMustache: z.boolean(),
  usesReadingGlasses: z.boolean(),
  accessoryName: z.string().nullable(),
});
export type KidsShotSessionAppearance = z.infer<typeof KidsShotSessionAppearanceSchema>;

// POST /api/kids/shot/capture → upsert one lighting slot. loggedTs = phone clock
// (epoch ms) at the shot — Pack matches the day's clips to captures by it.
// hasVideo: does a clip exist for this light? The V2 bulk-lux modal creates
// lux-only rows (false); the wizard after a recording and the V1 form (native
// camera — hence the default) send true. The row's stored flag only ever
// UPGRADES false→true (MAX in the upsert): a later lux edit can't un-shoot a
// light. Finish gates count only has_video=1 rows (bug 20.07: lux-only rows
// let the session go done after the first clip).
export const KidsShotCaptureReqSchema = z.object({
  sessionId: z.number().int(),
  lighting: KidsLightingSchema,
  lux: z.number().int().positive(),
  faceExpression: KidsFaceExpressionSchema.default(KIDS_DEFAULT_FACE_EXPRESSION),
  loggedTs: z.number().int().positive().optional(),
  hasVideo: z.boolean().default(true),
});
export type KidsShotCaptureReq = z.infer<typeof KidsShotCaptureReqSchema>;

export const KidsShotCaptureResSchema = z.object({
  ok: z.literal(true),
  luxValid: z.boolean(),
});
export type KidsShotCaptureRes = z.infer<typeof KidsShotCaptureResSchema>;

// POST /api/kids/shot/session/:id/captures → edit the captured light VALUES of an
// EXISTING session (the "session-values editor" — Сессии → Переснять). sessionId
// comes from the path; the body carries one entry per lighting the moderator
// changed (partial: only listed lights are written). Each entry updates just
// lux + faceExpression on that session's capture row — never loggedTs / photo_key
// / other fields. Only lights that already have a capture row are updated (this
// editor corrects values, it doesn't create missing slots — that's the Shot flow).
// Ownership + status gates live in the handler (own session for a moderator, any
// in office for admin/manager; a packed session is locked, canceled is frozen).
export const KidsShotCaptureEditItemSchema = z.object({
  lighting: KidsLightingSchema,
  lux: z.number().int().positive(),
  faceExpression: KidsFaceExpressionSchema,
});
export type KidsShotCaptureEditItem = z.infer<typeof KidsShotCaptureEditItemSchema>;

export const KidsShotCapturesEditReqSchema = z.object({
  captures: z.array(KidsShotCaptureEditItemSchema).min(1).max(4),
});
export type KidsShotCapturesEditReq = z.infer<typeof KidsShotCapturesEditReqSchema>;

export const KidsShotCapturesEditResSchema = z.object({
  ok: z.literal(true),
  // how many capture rows were actually updated (lights without an existing row
  // are skipped) — lets the UI note if a change targeted a not-yet-shot light.
  updated: z.number().int().nonnegative(),
});
export type KidsShotCapturesEditRes = z.infer<typeof KidsShotCapturesEditResSchema>;

// POST finish/cancel/delete — all take just a sessionId. `allowNoVideo` is read
// by FINISH only (cancel/delete ignore it): close a session whose 4 lux rows
// exist but were entered without in-app clips (has_video=0) — the moderator
// shot on the native camera and ships manually. The client shows an explicit
// confirm before sending it (Tim 20.07).
export const KidsShotSessionIdReqSchema = z.object({
  sessionId: z.number().int(),
  allowNoVideo: z.boolean().optional(),
});
export type KidsShotSessionIdReq = z.infer<typeof KidsShotSessionIdReqSchema>;

// ── GET /api/kids/shot/sessions?date=|from=&to= ───────────────────────────────
// The moderator's Сессии editor list. Shipped sessions (uploaded_at set) are
// EXCLUDED server-side (Tim 10.07): once clips are in R2 the session can't be
// edited or deleted, so it leaves the editor (management still sees it in the
// dashboard). ?date= keeps the legacy one-day behavior (device date OR
// office-local today); ?from=/&to= (YYYY-MM-DD, inclusive, either side
// optional) open past days / a period and take precedence over ?date=.
const SHOT_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const KidsShotSessionsQuerySchema = z.object({
  date: z.string().regex(SHOT_DATE_RE).optional(),
  from: z.string().regex(SHOT_DATE_RE).optional(),
  to: z.string().regex(SHOT_DATE_RE).optional(),
});
export type KidsShotSessionsQuery = z.infer<typeof KidsShotSessionsQuerySchema>;

// POST /api/kids/shot/revert → reopen session, or drop just one lighting slot.
export const KidsShotRevertReqSchema = z.object({
  sessionId: z.number().int(),
  lighting: KidsLightingSchema.optional(),
});
export type KidsShotRevertReq = z.infer<typeof KidsShotRevertReqSchema>;

export const KidsShotAckResSchema = z.object({ ok: z.literal(true) });
export type KidsShotAckRes = z.infer<typeof KidsShotAckResSchema>;

// POST /api/kids/shot/session/:id/finish → mark a stuck «В работе» (started)
// session done straight from the Сессии modal (it's not on the Съёмка board, so
// it can't be finished there). sessionId comes from the path; the body is empty.
// Unlike /shot/finish this does NOT require all 4 captures — a moderator marking
// a session done from Сессии is asserting the shoot is complete. It mirrors the
// Shot-flow finish's side effects exactly (started→done claim, background-set
// increment, debitForDoneSession, meeting→done recompute) and is idempotent:
// a re-finish of an already-done session returns ok without re-debiting.
// `allowNoVideo` mirrors /shot/finish: close a lux-complete session whose
// clips were shot on the native camera (has_video=0 rows) for manual shipping.
export const KidsShotFinishReqSchema = z.object({
  allowNoVideo: z.boolean().optional(),
});
export type KidsShotFinishReq = z.infer<typeof KidsShotFinishReqSchema>;

export const KidsShotFinishResSchema = z.object({
  ok: z.literal(true),
  // final session status so the client can reflect «Снято» without a refetch
  // (always 'done' on success — a done/idempotent finish both land here).
  status: z.literal('done'),
});
export type KidsShotFinishRes = z.infer<typeof KidsShotFinishResSchema>;

// POST /api/kids/shot/session/:id/photo → attach/replace the participant reference
// photo of an EXISTING session's child (#72 — «Add photo» in the editor). sessionId
// comes from the path; the handler resolves the child from the session, so the
// client never needs the childId. body.photo is a downscaled `data:image/…;base64,…`
// dataURL (maxSide 640, q 0.7), capped at 2MB of base64 (same as the shoot's
// KidsParticipantPhotoReqSchema). Stores in R2 (KIDS_CLIPS) + stamps
// children.photo_key. A packed session is NOT locked here — a photo is reference
// metadata, not shipped clip data. The photo bytes are PII — never logged.
export const KidsShotSessionPhotoReqSchema = z.object({
  photo: z.string().max(2_000_000),
});
export type KidsShotSessionPhotoReq = z.infer<typeof KidsShotSessionPhotoReqSchema>;

// On success the child now has a photo (hasPhoto=true drives the editor thumb).
export const KidsShotSessionPhotoResSchema = z.object({
  ok: z.literal(true),
  hasPhoto: z.literal(true),
});
export type KidsShotSessionPhotoRes = z.infer<typeof KidsShotSessionPhotoResSchema>;

// GET /api/kids/shot/stats → self-moderator counters for the Home/Pack screens
// (Tim's ТЗ: «сколько сессий за сегодня, сколько видео нужно отгрузить»).
// shotToday counts DONE sessions started within the caller's office-local today;
// toShip = done & not packed (all-time backlog); shipped = packed (all-time).
// Clip counts are sessions × 4 — the FE multiplies where it wants clip units.
export const KidsShotStatsResSchema = z.object({
  shotToday: z.number().int().nonnegative(),
  toShip: z.number().int().nonnegative(),
  shipped: z.number().int().nonnegative(),
});
export type KidsShotStatsRes = z.infer<typeof KidsShotStatsResSchema>;
